// ./src/utils/api/EntityAttachmentService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";
import { Car } from "./CarService";

// =============================================================================
// GraphQL Fragments
// =============================================================================

const ENTITY_ATTACHMENT_SCALAR_FIELDS = `
  id
  userId
  carId
  description
  attachmentType
  attachmentPath
  attachmentSize
  accessLevel
  forEntityTypeId
  coordinates
  uploadedFileId
  status
  createdAt
  updatedAt
`;

const ENTITY_ATTACHMENT_REF_FIELDS = `
  user {
    id
    username
    firstName
    lastName
  }
  car {
    id
    label
    plateNumber
  }
`;

// =============================================================================
// GraphQL Queries
// =============================================================================

const ENTITY_ATTACHMENT_LIST_QUERY = gql`
  query EntityAttachmentList(
    $filter: EntityAttachmentFilter
    $params: PaginationAndSorting
  ) {
    entityAttachmentList(filter: $filter, params: $params) {
      code
      data {
        ${ENTITY_ATTACHMENT_SCALAR_FIELDS}
        ${ENTITY_ATTACHMENT_REF_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ENTITY_ATTACHMENT_GET_QUERY = gql`
  query EntityAttachmentGet($id: ID!) {
    entityAttachmentGet(id: $id) {
      code
      data {
        ${ENTITY_ATTACHMENT_SCALAR_FIELDS}
        createdBy
        updatedBy
        user {
          id
          username
          firstName
          lastName
          email
        }
        car {
          id
          label
          plateNumber
          vin
        }
        userCreated {
          id
          username
          firstName
          lastName
        }
        userUpdated {
          id
          username
          firstName
          lastName
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ENTITY_ATTACHMENT_GET_MANY_QUERY = gql`
  query EntityAttachmentGetMany($ids: [ID]!) {
    entityAttachmentGetMany(ids: $ids) {
      code
      data {
        ${ENTITY_ATTACHMENT_SCALAR_FIELDS}
        ${ENTITY_ATTACHMENT_REF_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// GraphQL Mutations
// =============================================================================

const ENTITY_ATTACHMENT_CREATE_MUTATION = gql`
  mutation EntityAttachmentCreate($params: EntityAttachmentInput!) {
    entityAttachmentCreate(params: $params) {
      code
      data {
        ${ENTITY_ATTACHMENT_SCALAR_FIELDS}
        createdBy
        ${ENTITY_ATTACHMENT_REF_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ENTITY_ATTACHMENT_UPDATE_MUTATION = gql`
  mutation EntityAttachmentUpdate(
    $where: EntityAttachmentWhereInput!
    $params: EntityAttachmentInput!
  ) {
    entityAttachmentUpdate(where: $where, params: $params) {
      code
      data {
        ${ENTITY_ATTACHMENT_SCALAR_FIELDS}
        updatedBy
        ${ENTITY_ATTACHMENT_REF_FIELDS}
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ENTITY_ATTACHMENT_REMOVE_MUTATION = gql`
  mutation EntityAttachmentRemove($where: EntityAttachmentWhereInput!) {
    entityAttachmentRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ENTITY_ATTACHMENT_REMOVE_MANY_MUTATION = gql`
  mutation EntityAttachmentRemoveMany($where: [EntityAttachmentWhereInput]!) {
    entityAttachmentRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

export interface EntityAttachment {
  id?: string;
  userId?: string;
  carId?: string;
  description?: string;
  attachmentType?: number;
  attachmentPath?: string;
  attachmentSize?: string;
  accessLevel?: number;
  forEntityTypeId?: number;
  coordinates?: string;
  uploadedFileId?: string;
  status?: number;
  createdBy?: string;
  updatedBy?: string;
  removedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  user?: User;
  car?: Car;
  userCreated?: User;
  userUpdated?: User;
  userRemoved?: User;
}

export interface EntityAttachmentInput {
  id?: string;
  carId?: string;
  description?: string;
  attachmentType?: number;
  attachmentPath?: string;
  attachmentSize?: string;
  accessLevel?: number;
  forEntityTypeId?: number;
  coordinates?: string;
  uploadedFileId?: string;
  status?: number;
}

export interface EntityAttachmentFilter {
  id?: string[];
  userId?: string[];
  carId?: string[];
  attachmentType?: number[];
  accessLevel?: number[];
  forEntityTypeId?: number[];
  status?: number[];
  searchKeyword?: string;
}

export interface EntityAttachmentWhereInput {
  id?: string;
}

// =============================================================================
// Service Class
// =============================================================================

/**
 * EntityAttachment service that handles all attachment-related API calls
 * Supports CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class EntityAttachmentService {
  /**
   * Get a list of entity attachments with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EntityAttachmentFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(ENTITY_ATTACHMENT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single entity attachment by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(ENTITY_ATTACHMENT_GET_QUERY, {
      id,
    });
    return result;
  }

  /**
   * Get multiple entity attachments by their IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(ENTITY_ATTACHMENT_GET_MANY_QUERY, {
      ids,
    });
    return result;
  }

  /**
   * Create a new entity attachment
   */
  static async create(params: EntityAttachmentInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(ENTITY_ATTACHMENT_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing entity attachment
   */
  static async update(
    where: EntityAttachmentWhereInput,
    params: EntityAttachmentInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(ENTITY_ATTACHMENT_UPDATE_MUTATION, {
      where,
      params,
    });
    return result;
  }

  /**
   * Remove/delete an entity attachment by ID
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(ENTITY_ATTACHMENT_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple entity attachments by IDs
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      ENTITY_ATTACHMENT_REMOVE_MANY_MUTATION,
      {
        where: ids.map((id) => ({ id })),
      }
    );
    return result;
  }
}